<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'

// Modal dialog (prototype: centered sheet, scrim, pop-in). `alert` renders the compact
// confirmation style (role=alertdialog). Esc and the close button emit `close`.
const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    subtitle?: string
    closeLabel: string
    size?: 'medium' | 'large' | 'alert'
    dismissible?: boolean
  }>(),
  { size: 'large', dismissible: true, subtitle: undefined },
)

const emit = defineEmits<{ close: [] }>()

const titleId = `dialog-${useId()}`
const panel = ref<HTMLElement | null>(null)
let previouslyFocused: HTMLElement | null = null

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.dismissible) {
    event.stopPropagation()
    emit('close')
  } else if (event.key === 'Tab' && panel.value) {
    const focusable = panel.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (!first || !last) return
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      previouslyFocused = document.activeElement as HTMLElement | null
      document.body.style.overflow = 'hidden'
      await nextTick()
      const target = panel.value?.querySelector<HTMLElement>('[autofocus], input, select, textarea') ?? panel.value
      target?.focus()
    } else {
      document.body.style.overflow = ''
      previouslyFocused?.focus?.()
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="ui-dialog__scrim" @keydown="onKeydown">
      <div
        ref="panel"
        class="ui-dialog"
        :class="`ui-dialog--${size}`"
        :role="size === 'alert' ? 'alertdialog' : 'dialog'"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
      >
        <template v-if="size === 'alert'">
          <h2 :id="titleId" class="ui-dialog__alert-title">{{ title }}</h2>
          <div class="ui-dialog__alert-body"><slot /></div>
          <div class="ui-dialog__alert-actions"><slot name="footer" /></div>
        </template>
        <template v-else>
          <header class="ui-dialog__header">
            <div class="ui-dialog__heading">
              <h2 :id="titleId" class="ui-dialog__title">{{ title }}</h2>
              <span v-if="subtitle" class="ui-dialog__subtitle">{{ subtitle }}</span>
            </div>
            <button v-if="dismissible" type="button" class="ui-dialog__close" :aria-label="closeLabel" @click="emit('close')">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </header>
          <slot name="progress" />
          <div class="ui-dialog__body"><slot /></div>
          <footer v-if="$slots.footer" class="ui-dialog__footer"><slot name="footer" /></footer>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ui-dialog__scrim {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: var(--scrim);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: aub-fade-in 180ms var(--ease-standard);
}
.ui-dialog {
  background: var(--surface-card);
  color: var(--text-primary);
  border: var(--hairline) solid var(--separator);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 100%;
  animation: aub-pop-in 180ms var(--ease-standard);
  outline: none;
}
.ui-dialog--large,
.ui-dialog--medium {
  border-radius: var(--radius-list);
  max-height: min(820px, calc(100vh - 32px));
}
.ui-dialog--large {
  width: 960px;
}
.ui-dialog--medium {
  width: 760px;
}
.ui-dialog--alert {
  width: 420px;
  border-radius: var(--radius-sheet);
  padding: 24px;
  gap: 10px;
}
.ui-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 14px 24px;
  border-bottom: 1px solid var(--separator-soft);
}
.ui-dialog__heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.ui-dialog__title {
  margin: 0;
    font-size: var(--type-title-3-size);
  line-height: var(--type-title-3-line);
  font-weight: var(--weight-semibold);
}
.ui-dialog__subtitle {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
.ui-dialog__close {
  width: 32px;
  height: 32px;
  min-width: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: var(--radius-md);
  color: var(--tertiary-label);
  cursor: pointer;
}
.ui-dialog__close:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}
.ui-dialog__body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}
.ui-dialog__footer {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 24px;
  border-top: 1px solid var(--separator-soft);
  flex-wrap: wrap;
}
.ui-dialog__alert-title {
  margin: 0;
    font-size: var(--type-title-3-size);
  line-height: var(--type-title-3-line);
  font-weight: var(--weight-semibold);
}
.ui-dialog__alert-body {
  font-size: var(--type-body-size);
  line-height: var(--type-body-line);
  color: var(--text-secondary);
}
.ui-dialog__alert-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
  flex-wrap: wrap;
}
@media (max-width: 719px) {
  .ui-dialog__scrim {
    padding: 0;
    align-items: stretch;
  }
  .ui-dialog--large,
  .ui-dialog--medium {
    width: 100%;
    max-height: none;
    border-radius: 0;
  }
}
</style>
